import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  Loader2,
  ShoppingCart,
  Save,
  Trash2,
  Plus,
  X,
  Search,
} from "lucide-react";
import { API_BASE_URL, buildImageUrl } from '../../config';
import { LoadingOverlay } from '../LoadingSpinner';
import { getTokenCosts } from '../../services/api';
import './NewDesignContent.css';

function NewDesignContent({ onTokenUpdate, onCartUpdate, onAddedToCart }) {
  const { t } = useTranslation();

  // Design state
  const [designId, setDesignId] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [normalImage, setNormalImage] = useState(null);
  const [embroideryPreview, setEmbroideryPreview] = useState(null);

  // Token Costs - Only AI generation and order placement
  const [tokenCosts, setTokenCosts] = useState({
    ai_image_generation: 2,
    order_placement: 1
  });

  // AI Generation fields
  const [aiPrompt, setAiPrompt] = useState("");

  // Machine brand, file format and embroidery size are chosen once in the cart when
  // placing the order. Collecting them here too gave the order two sources of truth
  // that could disagree, which blocked admins from completing orders.

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const generationInFlightRef = useRef(false);

  // Load token costs and restore design state on component mount
  useEffect(() => {
    const loadTokenCosts = async () => {
      try {
        const data = await getTokenCosts();
        if (data && data.costs) {
          setTokenCosts(data.costs);
        }
      } catch (error) {
        console.error("Failed to load token costs:", error);
      }
    };
    
    const restoreDesignState = async () => {
      try {
        // Restore from localStorage if available
        const savedDesignId = localStorage.getItem("currentDesignId");

        if (savedDesignId) {
          setDesignId(savedDesignId);
          
          // Fetch the design to restore images
          const response = await fetch(`${API_BASE_URL}/designs/${savedDesignId}/`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            const design = data.design || data;
            
            // Restore images
            if (design.embroidery_preview) {
              setEmbroideryPreview(buildImageUrl(design.embroidery_preview));
            }
            if (design.normal_image) {
              setNormalImage(buildImageUrl(design.normal_image));
            }
          }
        }
      } catch (error) {
        console.error("Failed to restore design state:", error);
      }
    };
    
    loadTokenCosts();
    restoreDesignState();
  }, []);

  // Tab 1: Generate AI Image
  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) {
      setMessage(t("newDesign.pleaseEnterPrompt"));
      return;
    }

    if (generationInFlightRef.current || loading) {
      return;
    }

    generationInFlightRef.current = true;

    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/designs/generate-ai-image/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          design_id: designId || undefined,
          prompt: aiPrompt,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setDesignId(data.design.id);
        
        // Save design state to localStorage
        localStorage.setItem("currentDesignId", data.design.id);

        // Set embroidery preview (only image generated)
        if (data.design.embroidery_preview) {
          const embroideryUrl = buildImageUrl(data.design.embroidery_preview);
          setEmbroideryPreview(embroideryUrl);
        }
        
        if (onTokenUpdate) onTokenUpdate();
        setMessage(t("newDesign.generatedWithCost", { cost: tokenCosts.ai_image_generation }));
      } else {
        setMessage(`❌ ${data.error || t("newDesign.generationFailed")}`);
      }
    } catch (error) {
      console.error("AI generation error:", error);
      setMessage(t("newDesign.generationFailedRetry"));
    } finally {
      setLoading(false);
      generationInFlightRef.current = false;
    }
  };

  // Generate Embroidery Preview
  const handleGenerateEmbroideryPreview = async () => {
    if (!designId) {
      setMessage(t("newDesign.uploadOrGenerateFirst"));
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/designs/generate-embroidery-preview/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          design_id: designId,
        }),
      });

      const data = await response.json();
      
      console.log("🎨 Preview Response:", data);
      console.log("   embroidery_preview raw:", data.design?.embroidery_preview);

      if (response.ok && data.success) {
        const previewUrl = buildImageUrl(data.design.embroidery_preview);
        
        console.log("   embroidery_preview final:", previewUrl);
        setEmbroideryPreview(previewUrl);
        
        if (onTokenUpdate) onTokenUpdate();
        setMessage(t("newDesign.previewGenerated"));
      } else {
        setMessage(`❌ ${data.error || t("newDesign.previewGenerationFailed")}`);
      }
    } catch (error) {
      console.error("Preview generation error:", error);
      setMessage(t("newDesign.previewGenerationFailedRetry"));
    } finally {
      setLoading(false);
    }
  };

  // Save Design
  const handleSaveDesign = async () => {
    if (!designId) {
      setMessage(t("newDesign.noDesignToSave"));
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/designs/${designId}/update/`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: "My Design",
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage(t("newDesign.designSaved"));
      } else {
        setMessage(`❌ ${data.error || t("newDesign.saveFailed")}`);
      }
    } catch (error) {
      console.error("Save error:", error);
      setMessage(t("newDesign.saveFailedRetry"));
    } finally {
      setLoading(false);
    }
  };

  // Add to Cart
  const handleAddToCart = async () => {
    if (!designId) {
      setMessage(t("newDesign.noDesignToCart"));
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      // Format and size are picked in the cart, so there is nothing to save first.
      const response = await fetch(`${API_BASE_URL}/cart/add/${designId}/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (onTokenUpdate) onTokenUpdate();
        if (onCartUpdate) onCartUpdate();
        setMessage(t("newDesign.addedToCart"));
        // Send the customer to the cart, where they choose format and size and
        // place the order.
        if (onAddedToCart) onAddedToCart();
      } else {
        setMessage(`❌ ${data.error || t("newDesign.failedAddToCart")}`);
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      setMessage(t("newDesign.failedAddToCartRetry"));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setDesignId(null);
    setUploadedImage(null);
    setNormalImage(null);
    setEmbroideryPreview(null);
    setAiPrompt("");
    setMessage(t("newDesign.canvasCleared"));
    
    // Clear localStorage
    localStorage.removeItem("currentDesignId");
    // Stale keys from the removed Machine Settings tab.
    localStorage.removeItem("selectedMachineBrand");
    localStorage.removeItem("selectedFormat");
    localStorage.removeItem("embroiderySizeCm");
  };

  return (
    <>
      <LoadingOverlay visible={loading} text={t("newDesign.processing")}/>
      <div className="new-design-wrapper">
        {/* Canvas Area */}
        <div className="design-canvas-area">
        {/* Canvas/Content Area */}
        <div className="design-content-area">
            <div style={{ width: "100%", maxWidth: "800px" }}>
              {embroideryPreview ? (
                <div>
                  <div className="design-preview-grid">
                    <div className="design-preview-item">
                      <img
                        src={embroideryPreview}
                        alt={t("newDesign.embroideryPreviewAlt")}
                        className="design-preview-image"
                      />
                      <p style={{ marginTop: "10px", color: "#6B7280", fontSize: "13px", fontWeight: "500" }}>
                        {t("newDesign.embroideryPreview")}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <Sparkles size={48} color="#9CA3AF" style={{ margin: "0 auto 20px" }} />
                  <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#111827", marginBottom: "16px" }}>
                    {t("newDesign.generateWithAi")}
                  </h3>
                  <p style={{ color: "#6B7280", fontSize: "14px", marginBottom: "8px" }}>
                    {t("newDesign.enterPrompt")}
                  </p>
                </div>
              )}
            </div>
        </div>

        {/* Message Bar */}
        {message && (
          <div className={`design-message-bar ${message.includes("❌") ? 'error' : message.includes("⚠️") ? 'warning' : 'success'}`}>
            <span>{message}</span>
            <button
              onClick={() => setMessage("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "18px",
                padding: "0",
                color: "inherit",
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Right Panel */}
      <div className="design-right-panel">
        <div className="design-panel-content">
              <Section icon={Sparkles} title={t("newDesign.aiGeneration")}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#374151" }}>
                  {t("newDesign.prompt")}
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={t("newDesign.describePlaceholder")}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    fontSize: "13px",
                    marginBottom: "12px",
                    outline: "none",
                    fontFamily: "inherit",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                />





                <button
                  onClick={handleGenerateAI}
                  disabled={loading || !aiPrompt.trim()}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: loading || !aiPrompt.trim() ? "#93C5FD" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: loading || !aiPrompt.trim() ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      {t("newDesign.generating")}
                    </>
                  ) : (normalImage && embroideryPreview) ? (
                    <>
                      <Sparkles size={16} />
                      {t("newDesign.retryWithTokens", { count: tokenCosts.ai_image_generation })}
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      {t("newDesign.generateWithTokens", { count: tokenCosts.ai_image_generation })}
                    </>
                  )}
                </button>
              </Section>
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="design-panel-actions">
          <button
            onClick={handleSaveDesign}
            disabled={loading || !designId}
            className="design-action-btn primary"
          >
            <Save size={16} />
            {t("newDesign.saveDesign")}
          </button>

          <button
            onClick={handleAddToCart}
            disabled={loading || !designId}
            className="design-action-btn gradient"
          >
            <ShoppingCart size={16} />
            {t("newDesign.addToCart")}
          </button>

          <div className="design-action-buttons-row">
            <button
              onClick={handleClear}
              disabled={loading}
              className="design-action-btn secondary"
            >
              <Trash2 size={14} />
              {t("newDesign.clear")}
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
    </>
  );
}

// Helper Components
function Section({ icon: Icon, title, children }) {
  return (
    <div style={{ marginBottom: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
        <Icon size={16} color="#6B7280" />
        <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#374151", margin: 0 }}>
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function Input({ placeholder, value, onChange }) {
  return (
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        padding: "10px 12px",
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        fontSize: "13px",
        marginBottom: "12px",
        outline: "none",
        fontFamily: "inherit",
      }}
    />
  );
}

function InfoText({ children }) {
  return (
    <p style={{ fontSize: "12px", color: "#6B7280", margin: "8px 0" }}>
      {children}
    </p>
  );
}

function SettingRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "10px 0",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <span style={{ fontSize: "13px", color: "#374151" }}>{label}</span>
      <span style={{ fontSize: "13px", color: "#6B7280", fontWeight: "500" }}>{value}</span>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <label style={{ fontSize: "13px", fontWeight: "500", color: "#374151" }}>{label}</label>
      <ToggleSwitch checked={checked} onChange={onChange} />
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <label
      style={{
        position: "relative",
        display: "inline-block",
        width: "44px",
        height: "24px",
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: checked ? "#667eea" : "#D1D5DB",
          transition: "0.3s",
          borderRadius: "24px",
        }}
      >
        <span
          style={{
            position: "absolute",
            height: "18px",
            width: "18px",
            left: checked ? "23px" : "3px",
            bottom: "3px",
            background: "white",
            transition: "0.3s",
            borderRadius: "50%",
          }}
        />
      </span>
    </label>
  );
}

export default NewDesignContent;
